import Axios from "axios";
import React, { useState } from "react";
import { Button, Input, LoadingDots } from "web-ui";

import { languageChallengeApi } from "../api/languageChallenge";
import type { User } from "../api/v2";
import languages from "../data/languages.json";
import styles from "../styles/RegistrationPage.module.scss";
import type { LanguageId } from "../util/languages";
import { staticAssertNever } from "../util/types";

import Avatar from "./Avatar";
import { MegaFlag } from "./MegaFlag";

const confirmations = [
  "Do you want to register this account?",
  "Are you a Duolingo employee?",
  "Which course do you want to do the learning challenge in?",
  "Have you joined the #learning-challenge Slack channel?",
  "Will you give an honest effort to learn?",
] as const;
type Confirmation = (typeof confirmations)[number];

export interface Props {
  user: User;
  shownChallengeId: string;
}

interface HttpErrorBody {
  message: string;
}

const RegistrationPage = ({ user, shownChallengeId }: Props) => (
  <div className={styles.wrap}>
    <h2>Register for the Learning Challenge!</h2>
    <RegistrationForm shownChallengeId={shownChallengeId} user={user} />
  </div>
);

const RegistrationForm = ({ user, shownChallengeId }: Props) => {
  const [learningLanguage, fromLanguage, courseTitle] = (() => {
    if (user.learningLanguage === null) {
      // Assumes the user's learning language will only be null if they are in a math, music, or chess course. In this case, override the learning language to math, music, or chess. Use the same language ID for the from language since we don't care about UI language for Mega courses in the learning challenge.
      let megaLanguage: LanguageId;
      if (user.currentCourseId === "MATH_BT") {
        megaLanguage = "math";
      } else if (user.currentCourseId === "MUSIC_MT") {
        megaLanguage = "music";
      } else {
        megaLanguage = "chess";
      }
      return [megaLanguage, megaLanguage, languages[megaLanguage].name];
    } else {
      return [
        user.learningLanguage,
        user.fromLanguage,
        `${languages[user.learningLanguage].name} from ${
          languages[user.fromLanguage].name
        }`,
      ];
    }
  })();
  const initialConfirmationState = new Map<Confirmation, boolean>(
    confirmations.map(confirmation => [confirmation, false]),
  );
  const [confirmationsState, setConfirmationsState] = useState(
    initialConfirmationState,
  );
  const getOnConfirmationChange = (confirmation: Confirmation) => () => {
    const current = confirmationsState.get(confirmation) ?? false;
    // Must create new map object to trigger state change
    const newMap = new Map(confirmationsState);
    newMap.set(confirmation, !current);
    setConfirmationsState(newMap);
  };
  const [inputDuolingoEmailPrefix, setInputDuolingoEmailPrefix] = useState("");
  const onEmailInputchange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputDuolingoEmailPrefix(event.target.value);
  };
  const allConfirmed = ![...confirmationsState.values()].includes(false);
  const register = async () => {
    setShowLoadingInRegisterButton(true);
    try {
      await languageChallengeApi.postDuoRegistration(
        inputDuolingoEmailPrefix,
        learningLanguage,
        fromLanguage,
        shownChallengeId,
      );
      // TODO find a better way
      window.location.reload();
    } catch (e) {
      setShowLoadingInRegisterButton(false);
      if (
        Axios.isAxiosError(e) &&
        e.response?.status !== undefined &&
        e.response.status >= 400 &&
        e.response.status < 500
      ) {
        if (Object.hasOwnProperty.call(e.response.data, "message")) {
          alert((e.response.data as HttpErrorBody).message);
        } else {
          alert(e.response.data);
        }
      } else {
        alert("An error has occurred. Please try again later.");
        throw e;
      }
    }
  };
  const displayName = user.name ?? user.username;

  const [showLoadingInRegisterButton, setShowLoadingInRegisterButton] =
    useState(false);
  return (
    <div className={styles.box}>
      {confirmations.map(confirmation => {
        const onConfirmationChange = getOnConfirmationChange(confirmation);
        switch (confirmation) {
          case "Do you want to register this account?":
            return (
              <div className={styles["confirmation-item"]} key={confirmation}>
                <h3 className={styles["confirmation-title"]}>{confirmation}</h3>
                <p className={styles["confirmation-subtitle"]}>
                  This <b>does not</b> need to be your Duolingo admin account!
                  Not your account? Click{" "}
                  <a href="//duolingo.com/logout">here</a> to log out.
                </p>
                <div className={styles["confirmation-checkbox-row"]}>
                  <input
                    className={styles["confirmation-checkbox"]}
                    onChange={onConfirmationChange}
                    type="checkbox"
                  />
                  <Avatar className={styles.avatar} user={user} />
                  <div className={styles["name-column"]}>
                    <h3 className={styles.name}>{displayName}</h3>
                    {user.email && <p className={styles.email}>{user.email}</p>}
                  </div>
                </div>
              </div>
            );
          case "Are you a Duolingo employee?":
            return (
              <div className={styles["confirmation-item"]} key={confirmation}>
                <h3 className={styles["confirmation-title"]}>{confirmation}</h3>
                <div className={styles["confirmation-checkbox-row"]}>
                  <input
                    className={styles["confirmation-checkbox"]}
                    onChange={onConfirmationChange}
                    type="checkbox"
                  />
                  <p className={styles["confirmation-checkbox-text"]}>Yes!</p>
                </div>
              </div>
            );
          case "Which course do you want to do the learning challenge in?":
            return (
              <div className={styles["confirmation-item"]} key={confirmation}>
                <h3 className={styles["confirmation-title"]}>{confirmation}</h3>
                <p className={styles["confirmation-subtitle"]}>
                  If you want to do the challenge in a different course, switch
                  your course at <a href="//duolingo.com">duolingo.com</a> or on
                  the apps.
                </p>
                <div className={styles["confirmation-checkbox-row"]}>
                  <input
                    className={styles["confirmation-checkbox"]}
                    onChange={onConfirmationChange}
                    type="checkbox"
                  />
                  <MegaFlag className={styles.flag} lang={learningLanguage} />
                  <p className={styles["direction-text"]}>{courseTitle}</p>
                </div>
              </div>
            );
          case "Have you joined the #learning-challenge Slack channel?":
            return (
              <div className={styles["confirmation-item"]} key={confirmation}>
                <h3 className={styles["confirmation-title"]}>
                  Have you joined the{" "}
                  <a href="https://duolingo.slack.com/archives/CSQLHL05V">
                    #learning-challenge
                  </a>{" "}
                  Slack channel?
                </h3>
                <div className={styles["confirmation-checkbox-row"]}>
                  <input
                    className={styles["confirmation-checkbox"]}
                    onChange={onConfirmationChange}
                    type="checkbox"
                  />
                  <p className={styles["confirmation-checkbox-text"]}>Yes!</p>
                </div>
              </div>
            );
          case "Will you give an honest effort to learn?":
            return (
              <div className={styles["confirmation-item"]} key={confirmation}>
                <h3 className={styles["confirmation-title"]}>{confirmation}</h3>
                <div className={styles["confirmation-checkbox-row"]}>
                  <input
                    className={styles["confirmation-checkbox"]}
                    onChange={onConfirmationChange}
                    type="checkbox"
                  />
                  <p className={styles["confirmation-checkbox-text"]}>Yes!</p>
                </div>
              </div>
            );
          default:
            return staticAssertNever(confirmation);
        }
      })}

      <div className={styles["email-input-container"]}>
        <h3 className={styles["email-input-title"]}>
          What’s your Duolingo admin account email address?
        </h3>
        <p className={styles["email-input-subtitle"]}>
          It can be different from the account you’re registering for the
          challenge.
        </p>
        <div className={styles["email-input-row"]}>
          <Input
            className={styles["email-input-box"]}
            onChange={onEmailInputchange}
            type="text"
          />
          <p className={styles["email-input-suffix"]}>@duolingo.com</p>
        </div>
      </div>
      <Button
        className={styles.register}
        color="macaw"
        disabled={!allConfirmed || !inputDuolingoEmailPrefix}
        onClick={register}
        variant="solid"
      >
        {showLoadingInRegisterButton ? (
          <LoadingDots type="button" />
        ) : (
          "register"
        )}
      </Button>
    </div>
  );
};
export default RegistrationPage;
